import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Pencil, Plus, Send, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { DataTable, fmtDate } from "@/components/admin/DataTable";
import { ResourceForm, optKey, useOptions } from "@/components/admin/ResourceForm";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg, useApi } from "@/lib/api";
import { PANELS } from "@/pages/admin/ModulePanels";

const label = (k) => k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

function cell(row, k, fields, opts) {
  const v = row[k];
  const f = fields.find((x) => x.name === k);
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (f && (f.type.startsWith("ref:") || f.type === "course")) return (opts[optKey(f)] || []).find((o) => o.id === v)?.label || "—";
  if (/_at$/.test(k)) return fmtDate(v);
  if (Array.isArray(v)) return `${v.length} item(s)`;
  if (typeof v === "number" && /price|amount/.test(k)) return v.toLocaleString();
  return String(v).length > 80 ? `${String(v).slice(0, 80)}…` : String(v);
}

function ResourceTab({ res }) {
  const api = useApi();
  const opts = useOptions(res.fields);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [reload, setReload] = useState(0);
  const refresh = () => { setEdit(null); setReload((n) => n + 1); };
  const remove = async () => {
    try { await api.delete(`/admin/r/${res.key}/${del.id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  const send = async (row) => {
    try { const r = await api.post(`/admin/campaigns/${row.id}/send`); toast.success(`Sent ${r.data.sent}, not sent ${r.data.skipped}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const show = res.show || [res.title, ...res.fields.filter((f) => ["select", "bool", "number"].includes(f.type)).map((f) => f.name).slice(0, 4)];
  const columns = [
    ...show.map((k, i) => ({ key: k, label: res.fields.find((f) => f.name === k)?.label || label(k), sortable: true, defaultSort: i === 0 && false, render: (r) => cell(r, k, res.fields, opts) })),
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
    ...(res.can_write || res.actions.length ? [{ key: "_act", label: "Actions", render: (r) => (
      <div className="flex gap-1">
        {res.can_write && res.fields.length > 0 && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} aria-label="Edit" data-testid={`${res.key}-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>}
        {res.can_delete && <Button size="sm" variant="ghost" onClick={() => setDel(r)} aria-label="Delete" data-testid={`${res.key}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
        {res.actions.includes("send") && r.status !== "sent" && <Button size="sm" variant="outline" onClick={() => send(r)} data-testid={`${res.key}-send-${r.id}`}><Send className="mr-1 h-3 w-3" /> Send</Button>}
      </div>) }] : []),
  ];
  return (
    <div>
      {res.can_create && <div className="mb-3 flex justify-end"><Button onClick={() => setEdit({})} className="bg-[#222d99] hover:bg-[#1a237e]" data-testid={`${res.key}-add-btn`}><Plus className="mr-1 h-4 w-4" /> Add</Button></div>}
      <DataTable endpoint={`/admin/r/${res.key}`} columns={columns} rowKey="id" csvName={res.key} testid={`table-${res.key}`} reloadKey={reload} />
      <ResourceForm res={res} record={edit && edit.id ? edit : null} open={!!edit} onClose={() => setEdit(null)} onSaved={refresh} />
      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this record?</AlertDialogTitle><AlertDialogDescription>This can't be undone. The deletion is recorded in the audit log.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function ModulePage() {
  const { module } = useParams();
  const api = useApi();
  const [schema, setSchema] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/admin/schema").then((r) => setSchema(r.data)).catch((e) => setErr(errMsg(e))); }, [api]);
  if (err) return <p className="text-rose-700" data-testid="module-error">{err}</p>;
  if (!schema) return <Spinner />;
  const mod = schema.modules.find((m) => m.key === module);
  if (!mod) return <p className="rounded-xl border border-slate-200 bg-white p-6 text-slate-700" data-testid="module-denied">You don't have access to this page.</p>;
  const Panels = PANELS[module] || [];
  const tabs = [...Panels.map((p) => ({ key: p.key, label: p.label, node: <p.C /> })), ...mod.resources.map((r) => ({ key: r.key, label: r.label, node: <ResourceTab res={r} /> }))];
  return (
    <div data-testid={`module-${module}`}>
      <PageHeader eyebrow="Admin control centre" title={mod.label} />
      {schema.needs_2fa && <p className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="needs-2fa-banner"><ShieldAlert className="h-4 w-4" /> Turn on two-step verification in your account security settings to keep admin access.</p>}
      <Tabs defaultValue={tabs[0]?.key}>
        <TabsList className="h-auto flex-wrap justify-start" data-testid="module-tabs">
          {tabs.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        {tabs.map((t) => <TabsContent key={t.key} value={t.key} className="mt-4">{t.node}</TabsContent>)}
      </Tabs>
    </div>
  );
}
